/**
 * /cfo-advisor — board-level financial advisor (AI-FINANCE-AGENTS-001 P0).
 *
 * The CFO advisor is pinned to the strongest frontier model (Opus today)
 * and is deliberately READ-ONLY: it reads your books via read-only tools
 * and consults read-only specialist agents (close briefing, flux, accrual
 * drafter, bad-debt estimator, narrative writer). It never writes to the
 * ledger — so there is no write-consent gate here, only an advisory banner
 * reminding you to verify figures before acting.
 *
 * Like /chat, the CFO endpoint streams via SSE: POST /api/chat/cfo/stream
 * returns text + tool events incrementally. A board-style question fans out
 * to several specialists server-side, so streaming the specialist
 * consultations and prose live keeps a long turn legible.
 *
 * One CFO-specific wrinkle: the metric-from-code guard can rewrite the
 * final answer to strip a fabricated figure, and you can't un-send streamed
 * text. So the server streams the answer optimistically and, only when the
 * guard actually corrected a number, emits a `guard_correction {text}`
 * event — we swap the streamed bubble text for the authoritative version.
 */
import { appendStreamError, rememberStreamErrorRef, streamHttpError, withSupportRef } from "@/lib/supportRef";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ChangeEvent } from "react";

import { getToken } from "@/lib/api";
import { applyAgentNavigation } from "@/lib/agentNavigation";
import type { ChatMessage, ChatToolCallTrace } from "@/lib/types";

export const Route = createFileRoute("/_auth/cfo-advisor")({
  component: CfoAdvisorPage,
});

interface DisplayMessage {
  id: string;
  role: "user" | "assistant" | "system";
  text: string;
  tool_calls: ChatToolCallTrace[];
  streaming?: boolean;
  errored?: boolean;
  /** True once the metric guard swapped in a corrected answer. */
  corrected?: boolean;
  /** Import result card (from CSV upload) */
  importResult?: {
    format: string;
    imported: boolean;
    summary: string;
    link?: { label: string; href: string };
    errors?: string[];
  };
}

const SUGGESTED_PROMPTS = [
  "How is our cash runway looking?",
  "Walk me through last month's P&L and what changed.",
  "Are there any financial red flags I should know about?",
  "What's driving the change in our margins?",
  "If revenue stays flat, when do we run out of cash?",
  "Give me a board-ready summary of where we stand.",
];

function CfoAdvisorPage() {
  const router = useRouter();
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [uploading, setUploading] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // This page's conversation, saved like the Ask panel's: chat history, and
  // the record a "yes" in a later message is checked against (preview ->
  // confirm tools can't complete without it).
  const convIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  // appGenerated: the page wrote this message itself (e.g. after an import),
  // so it is saved as such and never counts as the person's own "yes".
  const submit = async (text: string, appGenerated = false) => {
    const trimmed = text.trim();
    if (!trimmed || streaming) return;

    const userMsg: DisplayMessage = {
      id: crypto.randomUUID(),
      role: "user",
      text: trimmed,
      tool_calls: [],
    };
    const assistantId = crypto.randomUUID();
    const assistantMsg: DisplayMessage = {
      id: assistantId,
      role: "assistant",
      text: "",
      tool_calls: [],
      streaming: true,
    };
    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setInput("");
    setStreaming(true);

    // Filter out system messages (import cards etc.) — they're display-only
    const history: ChatMessage[] = [...messages, userMsg]
      .filter((m) => m.role === "user" || m.role === "assistant")
      .map((m) => ({
        role: m.role as "user" | "assistant",
        content: m.text,
      }));

    try {
      if (!convIdRef.current) {
        try {
          const conv = await fetch("/api/chat/conversations", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken() ?? ""}` },
            body: JSON.stringify({ title: trimmed }),
          });
          if (conv.ok) convIdRef.current = (await conv.json()).id ?? null;
        } catch { /* persistence is best-effort; the answer still streams */ }
      }
      const response = await fetch("/api/chat/cfo/stream", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken() ?? ""}`,
        },
        body: JSON.stringify({
          messages: history,
          max_iterations: 10,
          conversation_id: convIdRef.current,
          app_generated: appGenerated,
        }),
      });

      if (!response.ok || !response.body) {
        // Carries the status and the server's support reference (FIX-LOG 93).
        throw await streamHttpError(response);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");

        let sepIdx: number;
        while ((sepIdx = buffer.indexOf("\n\n")) !== -1) {
          const raw = buffer.slice(0, sepIdx);
          buffer = buffer.slice(sepIdx + 2);
          const evt = parseSseEvent(raw);
          if (evt) {
            handleStreamEvent(assistantId, evt, setMessages);
            // The agent asked to open a screen — drive the app there.
            applyAgentNavigation(router, evt);
          }
        }
      }
    } catch (err) {
      const friendly = withSupportRef(friendlyChatError((err as Error).message), err, { surface: "cfo_chat" });
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId
            ? { ...m, streaming: false, errored: true, text: m.text || friendly }
            : m,
        ),
      );
    } finally {
      setStreaming(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit(input);
    }
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset so the same file can be re-selected after an error
    e.target.value = "";

    setUploading(true);
    const systemId = crypto.randomUUID();
    // Optimistic "uploading" placeholder
    setMessages((prev) => [
      ...prev,
      {
        id: systemId,
        role: "system",
        text: `Uploading ${file.name}…`,
        tool_calls: [],
      },
    ]);

    try {
      const form = new FormData();
      form.append("file", file);
      const resp = await fetch("/api/chat/upload-csv", {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken() ?? ""}` },
        body: form,
      });
      const data = await resp.json();
      if (!resp.ok) {
        const msg = (data as { detail?: string }).detail ?? `Upload failed (${resp.status})`;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === systemId
              ? { ...m, text: msg, errored: true }
              : m,
          ),
        );
        return;
      }
      // Replace placeholder with real result
      setMessages((prev) =>
        prev.map((m) =>
          m.id === systemId
            ? {
                ...m,
                text: data.summary ?? "Import complete.",
                importResult: {
                  format: data.format,
                  imported: data.imported,
                  summary: data.summary,
                  link: data.link,
                  errors: data.errors ?? [],
                },
              }
            : m,
        ),
      );
      // Auto-prompt the CFO agent about the imported data
      if (data.imported && data.format) {
        const autoPrompt =
          data.format === "carta"
            ? "My cap table was just imported. Summarize the ownership structure."
            : "My billing data was just imported. What's our current MRR and ARR?";
        setTimeout(() => submit(autoPrompt, true), 400);
      }
    } catch (err) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === systemId
            ? { ...m, text: `Upload failed: ${(err as Error).message}`, errored: true }
            : m,
        ),
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-56px)] flex-col bg-slate-50 dark:bg-gray-950">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-6 py-3 dark:border-gray-800 dark:bg-gray-900">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
          ✨ CFO Advisor
        </h1>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Board-level financial advice from your strongest model. Reads your
          books and consults specialist agents — but never changes anything.
        </p>
      </div>

      {/* Advisory banner — read-only, verify figures */}
      <div className="border-b border-blue-200 bg-blue-50 px-6 py-2 text-[12px] text-blue-800 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300">
        🔍 Read-only advisor. It can be wrong — verify the figures and reasoning
        before you act on them.
      </div>

      {/* Conversation */}
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto px-6 py-4">
        <div className="mx-auto max-w-4xl space-y-4">
          {messages.length === 0 && <EmptyState onPick={submit} />}
          {messages.map((m) => (
            <MessageBubble key={m.id} msg={m} />
          ))}
        </div>
      </div>

      {/* Composer */}
      <div className="border-t border-slate-200 bg-white px-6 py-3 dark:border-gray-800 dark:bg-gray-900">
        <div className="mx-auto max-w-4xl">
          <div className="flex items-end gap-2">
            {/* Hidden file input — triggered by the paperclip button */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.tsv,.txt"
              className="hidden"
              onChange={handleFileChange}
            />
            <button
              type="button"
              title="Import CSV (Carta cap table or Stripe billing)"
              disabled={streaming || uploading}
              onClick={() => fileInputRef.current?.click()}
              className="self-stretch rounded-md border border-slate-300 px-2.5 py-2 text-slate-500 hover:border-blue-400 hover:text-blue-600 disabled:opacity-40 dark:border-gray-700 dark:text-slate-400"
            >
              {uploading ? (
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
              )}
            </button>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask your CFO advisor anything about the business…"
              rows={2}
              disabled={streaming}
              className="flex-1 resize-none rounded-md border border-slate-300 px-3 py-2 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100 dark:placeholder-slate-500"
            />
            <button
              type="button"
              onClick={() => submit(input)}
              disabled={!input.trim() || streaming}
              className="self-stretch rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {streaming ? "Thinking…" : "Ask"}
            </button>
          </div>
          <p className="mt-2 text-[10px] text-slate-400 dark:text-slate-500">
            Every dollar figure comes from a live read of your books — the
            advisor can't invent numbers, but it can still err in judgment.
            {" "}
            <span title="Upload a CSV to import your cap table (Carta) or billing data (Stripe)">
              📎 = import CSV
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Error humanization (mirrors /chat)
// ──────────────────────────────────────────────────────────────
function friendlyChatError(detail: string): string {
  const lower = detail.toLowerCase();
  if (lower.includes("anthropic api key")) {
    return "I can't reach the AI — no Anthropic API key is configured. Set one at /settings, then ask again.";
  }
  if (lower.includes("credit balance is too low") || lower.includes("billing")) {
    return "The CFO advisor is temporarily unavailable — the Anthropic API credit balance is too low. Top up at console.anthropic.com/settings/billing, then try again.";
  }
  if (lower.includes("rate limit") || lower.includes("429")) {
    return "The AI is rate-limiting us. Try again in a moment.";
  }
  if (lower.includes("overloaded") || lower.includes("503")) {
    return "The AI is overloaded right now. Try again in a moment.";
  }
  const cleaned = detail.replace(/\{['"]type['"].*$/, "").trim();
  return `Sorry — that failed. ${cleaned || "Please try again."}`;
}

// ──────────────────────────────────────────────────────────────
// SSE handling (mirrors /chat, plus a guard_correction event)
// ──────────────────────────────────────────────────────────────
interface SseEvent {
  event: string;
  data: unknown;
}

function parseSseEvent(raw: string): SseEvent | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of raw.split("\n")) {
    if (line.startsWith(":")) continue;
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }
  if (dataLines.length === 0) return null;
  try {
    return { event, data: JSON.parse(dataLines.join("\n")) };
  } catch {
    return { event, data: dataLines.join("\n") };
  }
}

function handleStreamEvent(
  assistantId: string,
  evt: SseEvent,
  setMessages: React.Dispatch<React.SetStateAction<DisplayMessage[]>>,
) {
  const data = (evt.data ?? {}) as Record<string, unknown>;

  setMessages((prev) =>
    prev.map((m) => {
      if (m.id !== assistantId) return m;
      switch (evt.event) {
        case "text_delta":
          return { ...m, text: m.text + ((data.text as string) ?? "") };
        case "tool_use_start": {
          const newCall: ChatToolCallTrace = {
            tool: data.tool as string,
            arguments: (data.arguments as Record<string, unknown>) ?? {},
            result: null,
            error: null,
          };
          return { ...m, tool_calls: [...m.tool_calls, newCall] };
        }
        case "tool_use_result": {
          const targetTool = data.tool as string;
          const targetArgs = JSON.stringify(data.arguments ?? {});
          const updated = m.tool_calls.map((c, idx, arr) => {
            const isMatch =
              c.tool === targetTool &&
              JSON.stringify(c.arguments) === targetArgs &&
              c.result === null &&
              c.error === null;
            if (!isMatch) return c;
            const earlierMatch = arr
              .slice(0, idx)
              .some(
                (e) =>
                  e.tool === targetTool &&
                  JSON.stringify(e.arguments) === targetArgs &&
                  e.result === null &&
                  e.error === null,
              );
            if (earlierMatch) return c;
            return {
              ...c,
              result: (data.result as Record<string, unknown>) ?? null,
              error: (data.error as string) ?? null,
            };
          });
          return { ...m, tool_calls: updated };
        }
        case "guard_correction":
          // The metric guard rewrote the answer to strip an unsupported
          // figure — swap the streamed text for the authoritative version.
          return { ...m, text: (data.text as string) ?? m.text, corrected: true };
        case "done":
          return { ...m, streaming: false };
        case "error":
          rememberStreamErrorRef(data);
          return {
            ...m,
            streaming: false,
            errored: true,
            // A turn that fails part-way keeps what was said and still shows the
            // failure and its reference (FIX-LOG 93).
            text: appendStreamError(m.text, `Sorry — ${(data.error as string) ?? "stream failed."}`),
          };
        default:
          return m;
      }
    }),
  );
}

// ──────────────────────────────────────────────────────────────
// Subcomponents
// ──────────────────────────────────────────────────────────────
function EmptyState({ onPick }: { onPick: (s: string) => void }) {
  return (
    <div className="py-12 text-center text-slate-500 dark:text-slate-400">
      <div className="mb-2 text-3xl">📊</div>
      <p className="text-sm">
        Ask your CFO advisor about cash, margins, runway, or anything in the
        books.
      </p>
      <div className="mx-auto mt-6 flex max-w-3xl flex-wrap justify-center gap-2">
        {SUGGESTED_PROMPTS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onPick(p)}
            className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 hover:border-blue-400 hover:bg-blue-50 dark:border-gray-800 dark:bg-gray-900 dark:text-slate-300"
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ msg }: { msg: DisplayMessage }) {
  const isUser = msg.role === "user";
  const isSystem = msg.role === "system";

  // System messages: import result card or status line
  if (isSystem) {
    if (msg.importResult) {
      const { importResult: r } = msg;
      return (
        <div className="flex justify-center">
          <div className={`max-w-[90%] rounded-xl border px-4 py-3 text-sm ${
            !r.imported
              ? "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
              : "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200"
          }`}>
            <p className="font-medium">{r.summary}</p>
            {r.errors && r.errors.length > 0 && (
              <ul className="mt-2 space-y-0.5 text-xs opacity-80">
                {r.errors.map((e, i) => <li key={i}>⚠ {e}</li>)}
              </ul>
            )}
            {r.link && (
              <a
                href={r.link.href}
                className="mt-2 inline-block text-xs underline opacity-80 hover:opacity-100"
              >
                {r.link.label} →
              </a>
            )}
          </div>
        </div>
      );
    }
    // Plain status line (e.g., "Uploading…")
    return (
      <div className="flex justify-center">
        <p className={`text-xs italic ${msg.errored ? "text-red-500" : "text-slate-400 dark:text-slate-500"}`}>
          {msg.text}
        </p>
      </div>
    );
  }

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${
          isUser
            ? "bg-blue-600 text-white"
            : msg.errored
            ? "border border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200"
            : "border border-slate-200 bg-white text-slate-900 dark:border-gray-800 dark:bg-gray-900 dark:text-slate-100"
        }`}
      >
        {isUser ? (
          <span className="whitespace-pre-wrap">{msg.text}</span>
        ) : msg.streaming && !msg.text ? (
          <span className="italic text-slate-500 dark:text-slate-400">
            {msg.tool_calls.length > 0
              ? "Consulting specialists and reading your books…"
              : "Thinking…"}
            <span className="ml-1 inline-block h-3.5 w-1.5 animate-pulse bg-current align-middle" />
          </span>
        ) : (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
              h3: ({ children }) => <p className="mb-1 mt-3 font-semibold first:mt-0">{children}</p>,
              h2: ({ children }) => <p className="mb-1 mt-3 font-semibold first:mt-0">{children}</p>,
              ul: ({ children }) => <ul className="mb-2 list-disc space-y-0.5 pl-4">{children}</ul>,
              ol: ({ children }) => <ol className="mb-2 list-decimal space-y-0.5 pl-4">{children}</ol>,
              li: ({ children }) => <li className="leading-snug">{children}</li>,
              strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
              hr: () => <hr className="my-2 border-slate-200 dark:border-gray-700" />,
              table: ({ children }) => <div className="my-2 overflow-x-auto"><table className="w-full border-collapse text-xs">{children}</table></div>,
              th: ({ children }) => <th className="border border-slate-300 bg-slate-50 px-2 py-1 text-left font-medium dark:border-gray-600 dark:bg-gray-700">{children}</th>,
              td: ({ children }) => <td className="border border-slate-300 px-2 py-1 dark:border-gray-600">{children}</td>,
              code: ({ children }) => <code className="rounded bg-slate-100 px-1 font-mono text-xs dark:bg-gray-700">{children}</code>,
            }}
          >
            {msg.text}
          </ReactMarkdown>
        )}
        {!isUser && msg.corrected && (
          <div className="mt-2 text-[11px] text-amber-700 dark:text-amber-400">
            ↻ This answer was corrected to use only figures read from your books.
          </div>
        )}
        {!isUser && msg.tool_calls.length > 0 && (
          <ToolCallList calls={msg.tool_calls} />
        )}
      </div>
    </div>
  );
}

function ToolCallList({ calls }: { calls: ChatToolCallTrace[] }) {
  return (
    <details className="mt-3 border-t border-slate-200 pt-2 text-xs dark:border-gray-800">
      <summary className="cursor-pointer text-slate-500 hover:text-slate-700 dark:text-slate-400">
        🔧 {calls.length} {calls.length === 1 ? "consultation" : "consultations"}
      </summary>
      <ul className="mt-2 space-y-2">
        {calls.map((c, i) => (
          <li
            key={i}
            className={`rounded-md border p-2 ${
              c.error
                ? "border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30"
                : "border-slate-200 bg-slate-50 dark:border-gray-700 dark:bg-gray-800"
            }`}
          >
            <div className="flex items-center gap-1 font-mono text-[11px] text-slate-700 dark:text-slate-300">
              <span>{c.error ? "✗" : "✓"}</span>
              <span>
                {c.tool}({JSON.stringify(c.arguments)})
              </span>
            </div>
            {c.error && (
              <div className="mt-1 text-[11px] text-red-600">{c.error}</div>
            )}
            {c.result && (
              <details className="mt-1">
                <summary className="cursor-pointer text-[10px] text-slate-500 hover:text-slate-900 dark:text-slate-400">
                  Result
                </summary>
                <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded border border-slate-200 bg-white p-2 font-mono text-[10px] text-slate-800 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-200">
                  {JSON.stringify(c.result, null, 2)}
                </pre>
              </details>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
}
